import { useEffect, useState } from 'react';
import {
  loadWorkbookTabs,
  saveWorkbookTabs,
  type WorkbookTabs,
} from './workbookSession';
import { openTab } from './workbookTabs';

/** The open tabs of a Workbook, the one shown and the temporary one, kept across a reload. */
export function useWorkbookTabState(workbookId: string) {
  // Reopened on a reload, as a spreadsheet reopens the sheet you were on.
  const [restoredTabs] = useState(() => loadWorkbookTabs(workbookId));
  const [tabs, setTabs] = useState<WorkbookTabs>({
    opened: restoredTabs?.opened ?? ['process'],
    selected: restoredTabs?.selected ?? 'process',
    preview: restoredTabs?.preview,
  });
  const {
    opened: openedItems,
    selected: selection,
    preview: previewItem,
  } = tabs;
  useEffect(() => {
    saveWorkbookTabs(workbookId, tabs);
  }, [workbookId, tabs]);
  function setSelected(selected: string) {
    setTabs((current) => ({ ...current, selected }));
  }
  function setOpened(opened: string[]) {
    setTabs((current) => ({ ...current, opened }));
  }
  /**
   * Opens and shows `id`; made from the add tab `from`, in that tab's place.
   * A click in the sidebar only previews it (see `openTab`): it stays once
   * the tab is clicked or worked in. Computed from the latest tabs, so calls
   * made from a stale render (concurrent uploads finishing) all land.
   */
  function openItem(id: string, from?: string, preview = false) {
    setTabs((current) => openTab(current, id, from, preview));
  }
  /** Keeps `id` open past the next item opened. */
  function keepItem(id: string) {
    setTabs((current) =>
      current.preview === id ? { ...current, preview: undefined } : current,
    );
  }
  return {
    selection,
    setSelected,
    openedItems,
    setOpened,
    previewItem,
    openItem,
    keepItem,
  };
}
